import * as EventTracker from '@src/analytics/EventTracker';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef, useState } from 'react';

const logger = LogManager.getLogger('component:useRemoveDub');

/** The one repository call the flow needs — never the whole client store. */
export interface RemoveDubRepository {
  deleteDub(params: {
    storyId: string;
    targetLanguage: string;
  }): Promise<unknown>;
}

export interface UseRemoveDubOptions {
  storyId: string | null | undefined;
  repository: RemoveDubRepository;
  /** Human caption for a BCP-47 code, for the toasts. */
  captionForLanguage: (code: string) => string;
  showToast: (toast: { state: 'success' | 'error'; message: string }) => void;
  /**
   * The host's own follow-up once the dub is gone: drop the language from its
   * dubbed list (or re-read it), fall back to Original if it was listening to
   * that dub. Runs on success AND on "already gone" (the repository resolves
   * `null` for a 404), never on failure.
   */
  onRemoved: (language: string) => void;
}

export interface UseRemoveDubResult {
  /** The language staged for the confirm; null = no confirm open. */
  pendingLanguage: string | null;
  /** Stage the confirm for a language (the kebab's "Remove Dub"). */
  request: (language: string) => void;
  cancel: () => void;
  /** Run the removal for the staged language and close the confirm. */
  confirm: () => void;
  /**
   * PLAT-11877 — remove a language's dub with no confirm of its own, for a
   * host that already confirmed something larger ("Remove Language" removes
   * the translation and its dub). `silent` skips the success toast so the
   * larger action reports once; failures still toast. Resolves `false` when
   * the delete failed, so the caller can stop before orphaning anything.
   */
  remove: (
    language: string,
    options?: { silent?: boolean },
  ) => Promise<boolean>;
}

/**
 * PLAT-11663 — "Remove Dub", once, for every surface that offers it.
 *
 * Whole-language soft-delete via the backend (PLAT-10591): segments are
 * tombstoned but renditions survive (DI14), so a later re-dub reuses them. The
 * flow — stage a confirm, run the delete, tell analytics, toast the outcome —
 * used to live inline in the Transcript & Speakers tab (PLAT-10617); the clip
 * preview now offers the same entry, so the flow lives here and each host keeps
 * only what differs: how it renders the confirm and what it updates afterwards.
 *
 * Re-entrant-safe: a fast double-confirm fires one delete and one event.
 */
export function useRemoveDub({
  storyId,
  repository,
  captionForLanguage,
  showToast,
  onRemoved,
}: UseRemoveDubOptions): UseRemoveDubResult {
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const isRemovingRef = useRef(false);

  const run = useCallback(
    async (
      language: string,
      { silent = false }: { silent?: boolean } = {},
    ): Promise<boolean> => {
      if (isRemovingRef.current || !storyId) return false;
      isRemovingRef.current = true;
      try {
        await repository.deleteDub({ storyId, targetLanguage: language });
        onRemoved(language);
        EventTracker.trackDubRemoved({ story_id: storyId, language });
        if (!silent) {
          showToast({
            state: 'success',
            message: `${captionForLanguage(language)} dub removed`,
          });
        }
        return true;
      } catch (error) {
        logger.error('removeDub failed', {
          storyId,
          language,
          ...extractErrorInfo(error),
        });
        EventTracker.trackDubRemoveFailed({
          story_id: storyId,
          language,
          error_message: error instanceof Error ? error.message : String(error),
        });
        showToast({
          state: 'error',
          message: `Couldn't remove the ${captionForLanguage(language)} dub. Try again.`,
        });
        return false;
      } finally {
        isRemovingRef.current = false;
      }
    },
    [storyId, repository, captionForLanguage, showToast, onRemoved],
  );

  const request = useCallback((language: string) => {
    setPendingLanguage(language);
  }, []);
  const cancel = useCallback(() => setPendingLanguage(null), []);
  const confirm = useCallback(() => {
    if (!pendingLanguage) return;
    const language = pendingLanguage;
    setPendingLanguage(null);
    void run(language);
  }, [pendingLanguage, run]);

  return { pendingLanguage, request, cancel, confirm, remove: run };
}
